









import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import vueEcharts from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/component/title";
import "echarts/lib/component/tooltip";
import { EChartOption } from "echarts";
import { IAssessment } from "@/store/main.interfaces";
import { COLORS } from "@/colors";
import { i18n } from "../main";

type hoveredTableItemData = [string, number];

Vue.component("v-chart", vueEcharts);
@Component({
  components: { vueEcharts }
})
export default class BarChart extends Vue {
  @Prop() assessmentEntries!: IAssessment[];
  noDepartment: string = i18n.t("generics.terms.noDepartment") as string;
  @Prop() hoveredTableItem!: IAssessment;

  @Ref("chart") readonly chart!: any;

  @Watch("hoveredTableItem")
  onHoveredTableItemChange(val: IAssessment | null) {
    this.chart.dispatchAction({
      type: "downplay"
    });

    if (val) {
      const itemData: hoveredTableItemData = this.getHoveredTableItemData(val);
      this.chart.dispatchAction({
        type: "highlight",
        seriesName: itemData[0],
        dataIndex: itemData[1]
      });
    }
  }

  getHoveredTableItemData(item: IAssessment): hoveredTableItemData {
    const seriesName: string = item.department || this.$t("generics.terms.noDepartment") as string;
    const departmentEntries: IAssessment[] = this.assessmentEntries.filter(
            entry => entry.department == item.department
    );
    return [seriesName, departmentEntries.indexOf(item)];
  }

  getDepartments(): string[] {
    const departments = new Set<string>();
    this.assessmentEntries.forEach(entry =>
      departments.add(entry.department || this.noDepartment)
    );

    const allDepartments = Array.from(departments);
    const noDepartment = allDepartments.filter(
      department => department === this.noDepartment
    );
    const restDepartments = allDepartments
      .filter(department => department != this.noDepartment)
      .sort();

    return [...noDepartment, ...restDepartments];
  }

  generateSeries(): EChartOption.Series[] {
    const series: EChartOption.Series[] = this.assessmentEntries.map(
      assessment => {
        return {
          type: "bar",
          name: assessment.department || this.noDepartment,
          data: [assessment.outputs.effortToImplement],
          stack: assessment.department || this.noDepartment,
          tooltip: {
            formatter:
              "<strong>" +
              assessment.name +
              "</strong>" +
              "<br>" +
              i18n.t("generics.terms.freedCapacityShort") +
              "<strong>" +
              assessment.outputs.bandwidthFreed +
              "h" +
              "</strong>"
          }
        };
      }
    );

    const sortedSeries = this.sortSeries(series);

    this.addLabelToSeries(sortedSeries);
    this.generateItemStyle(sortedSeries);

    return sortedSeries;
  }

  sortSeries(series: any): EChartOption.Series[] {
    const noDepartmentSeries = series.filter(
      (series: { name: string }) => series.name === this.noDepartment
    );
    const departmentSeries = series
      .filter((series: { name: string }) => series.name != this.noDepartment)
      .sort((a: { name: string }, b: { name: string }) => {
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      });
    return [...noDepartmentSeries, ...departmentSeries];
  }

  addLabelToSeries(series: any) {
    const allDepartments = this.getDepartments();
    allDepartments.forEach(department => {
      const firstSeries = series
        .filter((series: { name: string }) => series.name === department)
        .shift();
      if (firstSeries) {
        firstSeries.label = {
          show: true,
          position: "bottom",
          formatter: department,
          color: "#828282",
          align: "left",
          rotate: -50
        };
      }
    });
  }

  generateItemStyle(series: any) {
    const allDepartments = this.getDepartments();

    const opacityMin = 0.1;
    const opacityMax = 1;
    const opacityMaxDiff = opacityMax - opacityMin;

    allDepartments.forEach((department, index) => {
      let opacity = 1;
      const seriesForDepartment = series.filter(
        (series: { name: string }) => series.name === department
      );

      const opacitySteps = opacityMaxDiff / seriesForDepartment.length;

      seriesForDepartment.forEach(
        (series: { itemStyle: { color: string; opacity: number } }) => {
          series.itemStyle = {
            color: COLORS[index],
            opacity: opacity
          };
          opacity -= opacitySteps;
        }
      );
    });
  }

  calculateYAxisMax(): number {
    const departments = this.getDepartments();
    const values: number[] = departments.map(department => {
      return this.getSumOfDepartment(department);
    });
    return Math.max(...values);
  }

  getSumOfDepartment(department: string): number {
    const sumOfHours = this.assessmentEntries
      .filter(entry => (entry.department || this.noDepartment) === department)
      .map(entry => {
        return typeof entry.outputs.effortToImplement === "number"
          ? entry.outputs.effortToImplement
          : 0;
      })
      .reduce((a, b) => a + b, 0);
    return sumOfHours;
  }

  get chartOptionsBar(): EChartOption {
    const generatedSeries = this.generateSeries();
    return {
      title: {
        show: false
      },
      color: COLORS,
      xAxis: {
        name: i18n.t("generics.terms.department") as string,
        nameLocation: "end",
        nameTextStyle: {
          color: "#828282",
          fontFamily: "Roboto"
        },
        axisLine: {
          lineStyle: {
            color: "#E0E0E0",
            width: 2
          }
        },
        type: "category"
      },
      yAxis: {
        name: i18n.t("time.hours") as string,
        nameLocation: "end",
        nameTextStyle: {
          color: "#828282",
          fontFamily: "Roboto"
        },
        axisLine: {
          lineStyle: {
            color: "#E0E0E0",
            width: 2
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          show: true,
          color: "#E0E0E0",
          fontFamily: "Roboto"
        },
        splitLine: {
          show: false
        },
        interval: 50,
        max: this.calculateYAxisMax,
        type: "value"
      },
      grid: {
        containLabel: true,
        right: 65,
        bottom: 100
      },
      series: [...generatedSeries],
      tooltip: {
        trigger: "item"
      }
    };
  }

  mouseover(event: any) {
    const id = event.data.id;
    this.$emit("mouseover-series", id);
  }

  mouseout(event: any) {
    const id = event.data.id;
    this.$emit("mouseout-series", id);
  }
}
